import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {$getSelection, $isRangeSelection, TextNode} from 'lexical';
import {useEffect} from 'react';

/**
 * Plugin that automatically replaces three consecutive periods (...) with
 * the Unicode ellipsis character (…).
 *
 * This prevents WAF (Web Application Firewall) rules from blocking requests
 * that contain ".." patterns in JSON bodies, which are often flagged as
 * potential path traversal attacks.
 */
export const EllipsisReplacementPlugin = () => {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    if (!editor) return;

    const removeTransform = editor.registerNodeTransform(TextNode, (node) => {
      const text = node.getTextContent();
      if (!text.includes('...') || text.includes('…')) return;

      // Get current selection to restore cursor position after replacement
      const selection = $getSelection();
      let cursorOffset: number | null = null;

      if ($isRangeSelection(selection) && selection.isCollapsed()) {
        const anchor = selection.anchor;
        if (anchor.key === node.getKey() && anchor.type === 'text') {
          cursorOffset = anchor.offset;
        }
      }

      // Replace all occurrences of "..." with "…"
      const newText = text.replace(/\.\.\./g, '…');

      // Calculate new cursor position: each "..." (3 chars) becomes "…" (1 char)
      // So we need to adjust the offset by counting how many "..." occur before the cursor
      if (cursorOffset !== null) {
        const textBeforeCursor = text.slice(0, cursorOffset);
        const ellipsisCount = (textBeforeCursor.match(/\.\.\./g) || []).length;
        // Each replacement reduces length by 2 (3 chars -> 1 char)
        cursorOffset -= ellipsisCount * 2;
      }

      node.setTextContent(newText);

      // Restore cursor position
      if (cursorOffset !== null && $isRangeSelection(selection)) {
        selection.anchor.set(node.getKey(), cursorOffset, 'text');
        selection.focus.set(node.getKey(), cursorOffset, 'text');
      }
    });

    return removeTransform;
  }, [editor]);

  return null;
};
